import { ProductKey } from 'src/shared/types/engine/products';

/**
 * A row on an organisation's product list.
 *
 * Only a `licensable` listing carries a `ProductKey`, because only those exist
 * in the engine's `product_key` enum and can actually be stored. The rest are
 * listed so the list is the whole picture rather than the part we happen to
 * have built — their access still comes from the project definition, and there
 * is nothing here to switch yet.
 *
 * A discriminated union rather than an `isLicensable` flag means a caller
 * cannot reach for a key the engine would reject.
 */
export type ProductListing = {
    id: string;
    label: string;
    /** Products that live under this one. A family, not a product in itself. */
    children?: ProductListing[];
} & ({ licensable: true; key: ProductKey } | { licensable: false });

/**
 * The product catalogue as an operator thinks of it.
 *
 * Lives beside the screen that renders it rather than in shared types: most of
 * it describes products the engine has never heard of, and filing it with the
 * engine's wire types would imply a relationship the union above exists to deny.
 *
 * Nature Performance and Global Nature Performance are different products that
 * happen to share most of a name, so they sit side by side at the top level
 * rather than one appearing to be a longer spelling of the other. Habitat
 * Insights, Species Insights and eDNA are members of Nature Performance, and
 * are nested to say so.
 */
export const PRODUCT_LISTINGS: ProductListing[] = [
    { id: 'global_nature_risk', label: 'Global Nature Risk', licensable: true, key: ProductKey.GLOBAL_NATURE_RISK },
    { id: 'global_nature_performance', label: 'Global Nature Performance', licensable: true, key: ProductKey.GLOBAL_NATURE_PERFORMANCE },
    {
        id: 'nature_performance',
        label: 'Nature Performance',
        licensable: false,
        children: [
            { id: 'habitat_insights', label: 'Habitat Insights', licensable: true, key: ProductKey.HABITAT_INSIGHTS },
            { id: 'edna', label: 'eDNA results', licensable: true, key: ProductKey.EDNA },
            { id: 'species_insights', label: 'Species Insights', licensable: true, key: ProductKey.SPECIES_INSIGHTS },
        ],
    },
];
